import { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Building2, Users as UsersIcon, TrendingUp, Layers, Activity, Power } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';

const Metric = ({ icon: Icon, label, value }) => (
  <Card className="glass p-4 border border-stone-800">
    <Icon className="w-6 h-6 text-amber-400 mb-1" />
    <p className="text-stone-400 text-xs uppercase tracking-wider">{label}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
  </Card>
);

const AdminOrganizations = () => {
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([
        axios.get(`${API}/admin/org-stats`),
        axios.get(`${API}/admin/organizations`),
      ]);
      setStats(s.data);
      setOrgs(o.data.organizations || []);
    } catch (e) {
      toast.error('Failed to load organization metrics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSub = async (org) => {
    const next = org.subscription_status === 'active' ? 'inactive' : 'active';
    try {
      await axios.post(`${API}/admin/organizations/${org.id}/toggle-subscription`, { subscription_status: next });
      toast.success(`${org.name} set to ${next}.`);
      setOrgs((prev) => prev.map((o) => (o.id === org.id ? { ...o, subscription_status: next } : o)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update subscription.');
    }
  };

  if (loading) return <p className="text-stone-500 py-6">Loading organization metrics...</p>;

  return (
    <div data-testid="admin-organizations-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Metric icon={Building2} label="Organizations" value={stats?.total_organizations ?? 0} />
        <Metric icon={Activity} label="Active Orgs" value={stats?.active_organizations ?? 0} />
        <Metric icon={UsersIcon} label="Org Athletes" value={stats?.total_org_athletes ?? 0} />
        <Metric icon={Layers} label="Teams" value={stats?.total_teams ?? 0} />
      </div>

      <Card className="glass p-4 border border-stone-800 mb-4">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-400" />
            <span className="text-stone-400 text-xs uppercase tracking-wider">Estimated MRR</span>
            <span className="text-2xl font-bold text-amber-400">${stats?.estimated_mrr ?? 0}</span>
          </div>
          <div className="flex gap-4 text-sm">
            {Object.entries(stats?.orgs_by_tier || {}).map(([tier, n]) => (
              <span key={tier} className="text-stone-300"><span className="text-white font-bold">{n}</span> <span className="text-stone-500 uppercase text-xs">{tier}</span></span>
            ))}
          </div>
          <div className="flex gap-4 text-sm">
            {Object.entries(stats?.orgs_by_type || {}).map(([t, n]) => (
              <span key={t} className="text-stone-300"><span className="text-white font-bold">{n}</span> <span className="text-stone-500 text-xs">{t.replace('_', ' ')}</span></span>
            ))}
          </div>
        </div>
      </Card>

      <Card className="glass p-4 border border-stone-800">
        <h2 className="text-lg font-bold text-amber-400 tracking-wider flex items-center gap-2 mb-4"><Building2 className="w-5 h-5" />ALL ORGANIZATIONS</h2>
        {orgs.length === 0 ? (
          <p className="text-stone-500 text-sm py-4">No organizations yet.</p>
        ) : (
          <div className="space-y-2">
            {orgs.map((o) => (
              <div key={o.id} className="glass rounded-lg border border-stone-800 p-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid={`admin-org-${o.id}`}>
                <div className="min-w-[160px]">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${o.subscription_status === 'active' ? 'bg-emerald-400' : 'bg-blood-500'}`} />
                    <span className="text-white font-bold">{o.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-amber-400 border border-amber-800 px-1.5 rounded">{o.plan_tier}</span>
                  </div>
                  <p className="text-[11px] text-stone-500">{o.type?.replace('_', ' ')} · {o.owner_email}</p>
                </div>
                <div className="text-xs text-stone-400">Athletes <span className="text-white font-bold">{o.athlete_count}</span></div>
                <div className="text-xs text-stone-400">Staff <span className="text-white font-bold">{o.staff_count}</span></div>
                <div className="text-xs text-stone-400">Teams <span className="text-white font-bold">{o.team_count}</span></div>
                <div className="text-xs text-stone-400">Active 7d <span className="text-emerald-400 font-bold">{o.active_athletes_7d}</span></div>
                <div className="text-xs text-stone-400">${o.monthly_price}/mo</div>
                <Button
                  size="sm"
                  onClick={() => toggleSub(o)}
                  className={`ml-auto font-semibold ${o.subscription_status === 'active' ? 'bg-blood-800 hover:bg-blood-700 text-white' : 'bg-emerald-800 hover:bg-emerald-700 text-white'}`}
                  data-testid={`toggle-org-sub-${o.id}`}
                >
                  <Power className="w-3.5 h-3.5 mr-1" />
                  {o.subscription_status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminOrganizations;
